import { useEffect, useMemo, useState } from "react";
import CopyButton, { chartText } from "./CopyButton";
import { Bar, Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend);

// Fallback only — used when the CSS custom properties can't be read (SSR, or a test environment with
// no real computed style). The live palette comes from --chart-1..6 in index.css, which the active
// theme repoints, so charts recolour along with the rest of the UI.
const FALLBACK = ["#16a34a", "#2563eb", "#f59e0b", "#ef4444", "#7c3aed", "#06b6d4"];

function readPalette() {
  if (typeof window === "undefined") return FALLBACK;
  const cs = getComputedStyle(document.documentElement);
  const vars = FALLBACK.map((fb, i) => (cs.getPropertyValue(`--chart-${i + 1}`) || "").trim() || fb);
  return vars;
}

/** Current theme id, re-read whenever the <html data-theme> attribute flips. Chart.js takes colours as
 * plain values baked into the config, so unlike the CSS-driven parts of the UI a chart can't recolour
 * itself — it has to be rebuilt. This hook is what triggers that rebuild. */
export function useThemeTick() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const obs = new MutationObserver(() => setTick((n) => n + 1));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  return tick;
}

export function buildChartConfig(spec, palette = readPalette()) {
  const COLORS = palette;
  const type = spec.type === "pie" ? "pie" : spec.type === "line" ? "line" : "bar";
  const labels = spec.labels || [];
  const datasets = (spec.series || []).map((s, i) => {
    if (type === "pie") {
      return { label: s.name || "", data: s.data || [], backgroundColor: labels.map((_, j) => COLORS[j % COLORS.length]) };
    }
    const c = COLORS[i % COLORS.length];
    return {
      label: s.name || "",
      data: s.data || [],
      backgroundColor: c + "cc",
      borderColor: c,
      borderWidth: 1.5,
      fill: false,
      tension: 0.3,
      borderRadius: 6,
    };
  });
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: { display: !!spec.title, text: spec.title || "", font: { size: 14, weight: "600" } },
      legend: { display: datasets.length > 1 || type === "pie", position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } },
    },
    scales:
      type === "pie"
        ? {}
        : {
            y: { beginAtZero: true, grid: { color: "#f1f5f9" }, ticks: { color: "#64748b", font: { size: 11 } } },
            x: { grid: { display: false }, ticks: { color: "#64748b", font: { size: 11 } } },
          },
  };
  return { type, data: { labels, datasets }, options };
}

// Below this many categories, bars/points stay comfortably readable at the card's normal width — no
// need to force a scrollbar for a 5-bar chart. Above it, compressing further makes bars unreadable, so
// the canvas grows and the box scrolls instead (same pattern `.table-box` already uses for wide tables).
const MIN_READABLE_CATEGORIES = 10;
const PX_PER_CATEGORY = 44;

/** Renders one chart spec: {type: 'bar'|'line'|'pie', title, labels, series: [{name, data}]}. */
export default function ChartWidget({ spec }) {
  const tick = useThemeTick();
  // `tick` is not read inside the callback — it's here purely to force a rebuild with the new
  // palette when the theme changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cfg = useMemo(() => buildChartConfig(spec), [spec, tick]);
  const Comp = cfg.type === "pie" ? Pie : cfg.type === "line" ? Line : Bar;
  const labelCount = cfg.data.labels.length;
  // A pie's size doesn't grow with category count the way a bar/line axis does, so it never needs the
  // scroll treatment — forcing a min-width on it would just add empty space beside the circle.
  const scrollable = cfg.type !== "pie" && labelCount > MIN_READABLE_CATEGORIES;
  return (
    <div className="chart-card">
      <CopyButton className="on-card" title="Copy chart data (pastes into Excel)" getText={() => chartText(spec)} />
      <div className="chart-box-outer">
        <div className="chart-box" style={scrollable ? { minWidth: labelCount * PX_PER_CATEGORY } : undefined}>
          <Comp data={cfg.data} options={cfg.options} />
        </div>
      </div>
    </div>
  );
}
